// src/services/adminApi.js
import { auth } from '../lib/firebase';
import { API_BASE_URL } from '../lib/apiBaseUrl';

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url, options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user available');
  }

  const token = await user.getIdToken();
  const isFormData = options.body instanceof FormData;
  const headers = {
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
};


// Top Picks Pool API endpoints
export const topPicksPoolApi = {
  // List Top Picks Pool records with filtering
  getTopPicksPool: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.age_group) queryParams.append('age_group', filters.age_group);
    if (filters.primary_genre) queryParams.append('primary_genre', filters.primary_genre);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const queryString = queryParams.toString();
    const url = `/admin/top-picks${queryString ? `?${queryString}` : ''}`;

    return makeAuthenticatedRequest(url);
  },

  // Preview derived Top Picks signals for a selected book
  previewTopPicksSignals: async (bookId) => {
    return makeAuthenticatedRequest(`/admin/top-picks/book-signals/${bookId}`);
  },

  // Create new Top Picks Pool record
  createTopPicksPoolRecord: async (data) => {
    return makeAuthenticatedRequest('/admin/top-picks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Update existing Top Picks Pool record
  updateTopPicksPoolRecord: async (id, data) => {
    return makeAuthenticatedRequest(`/admin/top-picks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Make record passive (soft disable)
  makeRecordPassive: async (id) => {
    return makeAuthenticatedRequest(`/admin/top-picks/${id}/passive`, {
      method: 'POST',
    });
  },

  // Refresh Top Picks (clears cached lists so they regenerate)
  refreshTopPicks: async () => {
    return makeAuthenticatedRequest('/admin/top-picks/refresh', {
      method: 'POST',
    });
  },
};

// New Releases API endpoints
export const newReleasesApi = {
  // Get New Releases lists with filtering
  getNewReleasesLists: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.age_group) queryParams.append('age_group', filters.age_group);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const queryString = queryParams.toString();
    const url = `/admin/new-releases/lists${queryString ? `?${queryString}` : ''}`;

    return makeAuthenticatedRequest(url);
  },

  // Create new New Releases list
  createNewReleasesList: async (data) => {
    return makeAuthenticatedRequest('/admin/new-releases/lists', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Add item to New Releases list
  addNewReleasesItem: async (data) => {
    return makeAuthenticatedRequest('/admin/new-releases/items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Update New Releases item
  updateNewReleasesItem: async (id, data) => {
    return makeAuthenticatedRequest(`/admin/new-releases/items/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Remove item from New Releases list
  removeNewReleasesItem: async (id) => {
    return makeAuthenticatedRequest(`/admin/new-releases/items/${id}`, {
      method: 'DELETE',
    });
  },

  // Publish New Releases list
  publishNewReleasesList: async (data) => {
    return makeAuthenticatedRequest('/admin/new-releases/publish', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Make New Releases list passive
  makeNewReleasesListPassive: async (data) => {
    return makeAuthenticatedRequest('/admin/new-releases/passive', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Duplicate New Releases list
  duplicateNewReleasesList: async (data) => {
    return makeAuthenticatedRequest('/admin/new-releases/duplicate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// Book of the Week API endpoints
export const bookOfTheWeekApi = {
  // Get Book of the Week records with filtering
  getBookOfTheWeekRecords: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.age_group) queryParams.append('age_group', filters.age_group);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const queryString = queryParams.toString();
    const url = `/admin/book-of-the-week${queryString ? `?${queryString}` : ''}`;

    return makeAuthenticatedRequest(url);
  },

  // Create new Book of the Week record (starts as draft)
  createBookOfTheWeekRecord: async (data) => {
    return makeAuthenticatedRequest('/admin/book-of-the-week', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Update a draft Book of the Week record (change book/note)
  updateBookOfTheWeekRecord: async (id, data) => {
    return makeAuthenticatedRequest(`/admin/book-of-the-week/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Publish Book of the Week record (make active)
  publishBookOfTheWeekRecord: async (id) => {
    return makeAuthenticatedRequest(`/admin/book-of-the-week/${id}/publish`, {
      method: 'PUT',
    });
  },

  // Make Book of the Week record passive
  makeBookOfTheWeekRecordPassive: async (id) => {
    return makeAuthenticatedRequest(`/admin/book-of-the-week/${id}/passive`, {
      method: 'POST',
    });
  },

  // Get fallback record (age_group = 'all')
  getFallbackRecord: async () => {
    return makeAuthenticatedRequest('/admin/book-of-the-week/fallback');
  },

  // Create fallback record (age_group = 'all')
  createFallbackRecord: async (data) => {
    return makeAuthenticatedRequest('/admin/book-of-the-week/fallback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Delete Book of the Week record (draft only)
  deleteBookOfTheWeekRecord: async (id) => {
    return makeAuthenticatedRequest(`/admin/book-of-the-week/${id}`, {
      method: 'DELETE',
    });
  },
};

// Curated Lists (admin-managed editorial lists) API endpoints
export const curatedListsApi = {
  getLists: async () => {
    return makeAuthenticatedRequest('/admin/curated-lists');
  },

  createList: async (data) => {
    return makeAuthenticatedRequest('/admin/curated-lists', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateList: async (id, data) => {
    return makeAuthenticatedRequest(`/admin/curated-lists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteList: async (id) => {
    return makeAuthenticatedRequest(`/admin/curated-lists/${id}`, {
      method: 'DELETE',
    });
  },

  getItems: async (id) => {
    return makeAuthenticatedRequest(`/admin/curated-lists/${id}/items`);
  },

  addItem: async (id, bookId) => {
    return makeAuthenticatedRequest(`/admin/curated-lists/${id}/items`, {
      method: 'POST',
      body: JSON.stringify({ book_id: bookId }),
    });
  },

  removeItem: async (id, itemId) => {
    return makeAuthenticatedRequest(`/admin/curated-lists/${id}/items/${itemId}`, {
      method: 'DELETE',
    });
  },
};

// Discover Author spotlight API endpoints
export const authorSpotlightApi = {
  getLists: async () => {
    return makeAuthenticatedRequest('/admin/author-spotlight/lists');
  },

  updateSpotlight: async (id, isAuthorSpotlight) => {
    return makeAuthenticatedRequest(`/admin/author-spotlight/lists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ is_author_spotlight: isAuthorSpotlight }),
    });
  },
};

// Books Library API endpoints
// Note: The book search endpoint is intentionally public (no authentication required)
// while other book operations use admin authentication. This allows searching without
// admin tokens while protecting admin-only operations.
export const booksLibraryApi = {
  // Search books (public endpoint - no authentication required)
  searchBooks: async (query) => {
    if (!query || !query.trim()) {
      return { books: [] };
    }

    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 3) {
      throw new Error('Search query must be at least 3 characters long');
    }

    const response = await fetch(`${API_BASE_URL}/books/search?q=${encodeURIComponent(trimmedQuery)}`);

    if (!response.ok) {
      throw new Error(`Search failed: HTTP ${response.status}`);
    }

    const result = await response.json();

    // Ensure consistent response format
    if (Array.isArray(result)) {
      return { books: result };
    } else if (result && Array.isArray(result.books)) {
      return result;
    } else {
      console.warn('Unexpected search response format:', result);
      return { books: [] };
    }
  },
};

export const adminBooksApi = {
  getBookDetail: async (bookId) => {
    return makeAuthenticatedRequest(`/admin/books/${bookId}`);
  },

  updateBookFields: async (bookId, data) => {
    return makeAuthenticatedRequest(`/admin/books/${bookId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  previewBookTags: async (bookId, data) => {
    return makeAuthenticatedRequest(`/admin/books/${bookId}/generate-tags-preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  saveBookTags: async (bookId, data) => {
    return makeAuthenticatedRequest(`/admin/books/${bookId}/tags`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};

// Dashboard API endpoints
export const dashboardApi = {
  // Get dashboard summary (counts for all editorial modules)
  getSummary: async () => {
    return makeAuthenticatedRequest('/admin/dashboard/summary');
  },

  // Get dashboard warnings and health indicators
  getWarnings: async () => {
    return makeAuthenticatedRequest('/admin/dashboard/warnings');
  },

  // Get recent activity
  getRecentActivity: async (limit = 20) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    const url = `/admin/dashboard/recent-activity${queryString ? `?${queryString}` : ''}`;
    return makeAuthenticatedRequest(url);
  },
};

// Logs API endpoints
// Note: Uses the existing dashboard recent-activity endpoint as the backend
// does not yet have a dedicated logging system. This provides operational
// visibility through editorial record changes.
export const logsApi = {
  // Get logs/activity data (uses dashboard recent activity endpoint)
  getLogs: async (options = {}) => {
    const { limit = 50 } = options;
    const params = new URLSearchParams();

    // For now, we use the dashboard recent-activity endpoint
    // In the future, this could be extended to a dedicated logs endpoint
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = `/admin/dashboard/recent-activity${queryString ? `?${queryString}` : ''}`;

    const result = await makeAuthenticatedRequest(url);

    // Transform the dashboard activity format to logs format for consistency
    return {
      logs: result.activities || [],
      totalCount: result.count || 0,
      generatedAt: result.generatedAt,
      // Note: Pagination and filtering are client-side for V1
      // since the backend endpoint doesn't support them yet
      hasNextPage: false,
      hasPrevPage: false
    };
  },
};

const buildBulkImportFormData = (file, options = null) => {
  const formData = new FormData();
  formData.append('file', file);

  if (options) {
    formData.append('generateTags', String(options.generateTags));
    formData.append('forceUpdate', String(options.forceUpdate));
    formData.append('forceRetag', String(options.forceRetag));
  }

  return formData;
};

export const bulkImportApi = {
  runDryRun: async (file) => {
    return makeAuthenticatedRequest('/admin/books/bulk-import/dry-run', {
      method: 'POST',
      body: buildBulkImportFormData(file),
    });
  },

  startImport: async (file, options) => {
    return makeAuthenticatedRequest('/admin/books/bulk-import', {
      method: 'POST',
      body: buildBulkImportFormData(file, options),
    });
  },

  getJobStatus: async (jobId) => {
    return makeAuthenticatedRequest(`/admin/jobs/${jobId}`);
  },

  getJobResults: async (jobId, status = 'all') => {
    const params = new URLSearchParams();
    if (status && status !== 'all') {
      params.append('status', status);
    }

    const queryString = params.toString();
    const url = `/admin/jobs/${jobId}/results${queryString ? `?${queryString}` : ''}`;
    return makeAuthenticatedRequest(url);
  },

  retryFailed: async (jobId, forceRetag = false) => {
    return makeAuthenticatedRequest(`/admin/jobs/${jobId}/retry-failed`, {
      method: 'POST',
      body: JSON.stringify({ forceRetag }),
    });
  },
};

export const enrichmentApi = {
  startBulkEnrichment: async () => {
    return makeAuthenticatedRequest('/admin/enrichment/jobs', {
      method: 'POST',
    });
  },

  getEnrichmentJob: async (jobId) => {
    return makeAuthenticatedRequest(`/admin/enrichment/jobs/${jobId}`);
  },

  getEnrichmentJobResults: async (jobId, page = 1, limit = 50) => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));

    return makeAuthenticatedRequest(`/admin/enrichment/jobs/${jobId}/results?${params.toString()}`);
  },

  enrichBook: async (bookId) => {
    return makeAuthenticatedRequest(`/admin/books/${bookId}/enrichment`, {
      method: 'POST',
    });
  },

  getBookEnrichment: async (bookId) => {
    return makeAuthenticatedRequest(`/admin/books/${bookId}/enrichment`);
  },
};
